import React, { lazy, useRef, memo, useCallback, useEffect, Suspense } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Route, Switch } from 'react-router';
import URLs from '~/enums/URLs';
import { NoMatch } from '../noMatch/NoMatch';
import { MainContentProps } from './models';
import { datadogRum } from '@sunbit-dev/common/services/datadog';
import styled from 'styled-components';
import classNames from 'classnames';
import * as styles from './styles';
import { PagesTestIds } from '~/enums/TestIds';
import { MessagePage } from '../messagePage/MessagePage';
import messageService from '~/services/MessageService';
import { useSelector } from 'react-redux';
import * as purchaseStore from '~/store/reducers/purchase/purchase';
import PlaceInFlowPages from '~/services/MessageService/PlaceInFlowPages';
import pageName from '~/services/MessageService/PlaceInFlowToUrlMap';
import { LoanApplicationOrigin } from '~/enums/LoanApplicationOrigin';
import { EventsNames } from '~/enums/Events';

const lazyWithRetry = (componentImport) =>
  lazy(async () => {
    const pageHasAlreadyBeenForceRefreshed = JSON.parse(
      window.localStorage.getItem('page-has-been-force-refreshed') || 'false'
    );

    try {
      const component = await componentImport();
      window.localStorage.setItem('page-has-been-force-refreshed', 'false');
      return component;
    } catch (error) {
      if (!pageHasAlreadyBeenForceRefreshed) {
        window.localStorage.setItem('page-has-been-force-refreshed', 'true');
        datadogRum.addError(new Error(EventsNames.componentForcedRefresh));
        window.location.reload();
        return new Promise(() => {});
      } else {
        datadogRum.addError(new Error(EventsNames.componentFailedToLoadAfterForcedRefresh));
        throw error;
      }
    }
  });

const DigitalEstimateThankYouPage = lazyWithRetry(() =>
  import('../digitalEstimateThankYouPage/DigitalEstimateThankYouPage.component').then((module) => ({
    default: module.DigitalEstimateThankYouPage,
  }))
);

const CustomerDetailsPage = lazyWithRetry(() =>
  import('../customerDetailsPage/CustomerDetailsPage').then((module) => ({ default: module.CustomerDetailsPage }))
);

const ChooseYourPlanPage = lazyWithRetry(() =>
  import('../chooseYourPlanPage/ChooseYourPlanPage').then((module) => ({ default: module.ChooseYourPlanPage }))
);

const PaymentDetailsPage = lazyWithRetry(() =>
  import('../paymentDetailsPage/PaymentDetailsPage').then((module) => ({ default: module.PaymentDetailsPage }))
);

const AgreementPage = lazyWithRetry(() =>
  import('../agreementPage/AgreementPage').then((module) => ({ default: module.AgreementPage }))
);

const FinalReviewPage = lazyWithRetry(() =>
  import('../finalReviewPage/FinalReviewPage').then((module) => ({ default: module.FinalReviewPage }))
);

const ProcessingPaymentPage = lazyWithRetry(() =>
  import('../processingPaymentPage/ProcessingPaymentPage').then((module) => ({ default: module.ProcessingPaymentPage }))
);

const PostChargeFailedPage = lazyWithRetry(() =>
  import('../postChargeFailedPage/PostChargeFailedPage').then((module) => ({ default: module.PostChargeFailedPage }))
);

const CustomerVerificationPage = lazyWithRetry(() =>
  import('../customerVerificationPage/CustomerVerificationPage').then((module) => ({
    default: module.CustomerVerificationPage,
  }))
);

const CustomerVerifiedPage = lazyWithRetry(() =>
  import('../customerVerifiedPage/CustomerVerifiedPage').then((module) => ({ default: module.CustomerVerifiedPage }))
);

const EmailVerificationPage = lazyWithRetry(() =>
  import('../emailVerificationPage/EmailVerificationPage').then((module) => ({
    default: module.EmailVerificationPage,
  }))
);

const OptionsLoadingPage = lazyWithRetry(() =>
  import('../optionsLoadingPage/OptionsLoadingPage').then((module) => ({ default: module.OptionsLoadingPage }))
);

const InfoPages = lazyWithRetry(() => import('../info/InfoPages').then((module) => ({ default: module.InfoPages })));

const ThankYouPage = lazyWithRetry(() =>
  import('../thankYouPage/ThankYouPage').then((module) => ({ default: module.ThankYouPage }))
);

const DeclinePage = lazyWithRetry(() =>
  import('../declinePage/DeclinePage').then((module) => ({ default: module.DeclinePage }))
);

const ExtraVerificationDeclinedPage = lazyWithRetry(() =>
  import('../extraVerificationDeclinedPage/ExtraVerificationDeclinedPage').then((module) => ({
    default: module.ExtraVerificationDeclinedPage,
  }))
);

const ExpiredPage = lazyWithRetry(() =>
  import('../expiredLinkPage/ExpiredLinkPage').then((module) => ({ default: module.ExpiredLinkPage }))
);

const IntroPage = lazyWithRetry(() =>
  import('../introPage/IntroPage').then((module) => ({ default: module.IntroPage }))
);

const PurchaseAmountPendingPage = lazyWithRetry(() =>
  import('../purchaseAmountPendingPage/PurchaseAmountPendingPage').then((module) => ({
    default: module.PurchaseAmountPendingPage,
  }))
);

const PurchaseAmountNotConfirmedPage = lazyWithRetry(() =>
  import('../purchaseAmountNotConfirmedPage/PurchaseAmountNotConfirmedPage').then((module) => ({
    default: module.PurchaseAmountNotConfirmedPage,
  }))
);

const ValidateDebitCardPage = lazyWithRetry(() =>
  import('../validateDebitCardPage/ValidateDebitCardPage').then((module) => ({ default: module.ValidateDebitCardPage }))
);

const CreditFreezePage = lazyWithRetry(() =>
  import('../creditFreezePage/CreditFreezePage').then((module) => ({
    default: module.CreditFreezePage,
  }))
);

export const MainContent = styled(
  memo(({ className }: MainContentProps) => {
    const { pathname } = useLocation();
    const { offer, purchaseInfo } = useSelector(purchaseStore.getStore);

    const wrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      wrapperRef?.current.scrollTo({ top: 0 });
      datadogRum.startView(pathname);
    }, [pathname]);

    useEffect(() => {
      if (purchaseInfo?.loanApplicationOrigin === LoanApplicationOrigin.ONLINE_SDK && pageName.get(pathname)) {
        messageService.messagePlaceInFlow(pageName.get(pathname) as PlaceInFlowPages, offer?.offerId.toString());
      }
    }, [pathname, purchaseInfo, offer]);

    const history = useHistory();

    const onInfoBackButtonClicked = useCallback(() => {
      history.goBack();
    }, [history]);

    return (
      <div
        className={classNames(className, { 'no-padding': pathname === URLs.Intro })}
        ref={wrapperRef}
        data-testid={PagesTestIds.MainContent}
      >
        {/* Removed the flickering loader on lazy loading (fallback="") */}
        <Suspense fallback="">
          <Switch>
            <Route path={URLs.CustomerDetails} render={() => <CustomerDetailsPage />} exact />
            <Route path={URLs.EmailVerification} render={() => <EmailVerificationPage />} exact />
            <Route path={URLs.CustomerVerification} render={() => <CustomerVerificationPage />} exact />
            <Route path={URLs.CustomerVerified} render={() => <CustomerVerifiedPage />} exact />
            <Route path={URLs.Intro} render={() => <IntroPage />} exact />
            <Route path={URLs.Evnoia} render={() => <ExtraVerificationDeclinedPage />} exact />
            <Route path={URLs.OptionsLoading} render={() => <OptionsLoadingPage />} exact />
            <Route path={URLs.ChooseYourPlan} render={() => <ChooseYourPlanPage />} exact />
            <Route path={URLs.PurchaseAmountPending} render={() => <PurchaseAmountPendingPage />} exact />
            <Route path={URLs.PurchaseAmountNotConfirmed} render={() => <PurchaseAmountNotConfirmedPage />} exact />
            <Route path={URLs.PaymentDetails} render={() => <PaymentDetailsPage />} exact />
            <Route path={URLs.Agreement} render={() => <AgreementPage />} exact />
            <Route path={URLs.Review} render={() => <FinalReviewPage />} exact />
            <Route path={URLs.ProcessingPayment} render={() => <ProcessingPaymentPage />} exact />
            <Route path={URLs.PostChargeFailed} render={() => <PostChargeFailedPage />} exact />
            <Route path={URLs.Thanks} render={() => <ThankYouPage />} exact />
            <Route path={URLs.EstimateThankYou} render={() => <DigitalEstimateThankYouPage />} exact />
            <Route path={URLs.DebitCardValidation} render={() => <ValidateDebitCardPage />} exact />
            <Route path={URLs.LinkExpired} render={() => <ExpiredPage />} exact />
            <Route path={URLs.CreditFreeze} render={() => <CreditFreezePage />} exact />
            <Route
              path={`${URLs.Decline}/:reason`}
              render={(args) => {
                return <DeclinePage {...args} />;
              }}
              exact
            />
            <Route path={`${URLs.Message}/:type`} render={() => <MessagePage />} exact />
            <Route
              path={URLs.Info}
              render={(routeProps) => <InfoPages {...routeProps} onBackButtonClicked={onInfoBackButtonClicked} />}
            />

            <Route component={NoMatch} />
          </Switch>
        </Suspense>
      </div>
    );
  })
)`
  ${styles.MainContent}
`;
